import React, { useMemo } from 'react';

import { AnyAri } from '@atlassian/ari/any-ari';
import {
	isBranchEntity,
	isCalendarEventEntity,
	isCommentEntity,
	isCommitEntity,
	isConversationEntity,
	isCustomerOrgEntity,
	isDashboardEntity,
	isDataTableEntity,
	isDealEntity,
	isDesignEntity,
	isDocumentEntity,
	isMessageEntity,
	isPullRequestEntity,
	isRepositoryEntity,
	isVideoEntity,
	isWorkItemEntity,
	type SearchResultEntity,
	type SearchResultGraphDocument,
} from '@atlassian/search-client';

import { IconFor3P } from '../../../../../../common/ui/icon-for-3p';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import {
	getIntegrationKey,
	getProductFromProviderId,
} from '../../../../../../common/utils/search-result-utils';
import {
	getResultConnectorSource,
	isResultSmartLink,
} from '../../../../../../common/utils/third-party-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useBootstrap } from '../../../../../../controllers/store/bootstrap';
import { useSelectedProducts } from '../../../../../../controllers/store/search';

import { BranchSearchResult } from './branch-search-result';
import { CalendarEventSearchResult } from './calendar-event-search-result';
import { CommentSearchResult } from './comment-search-result';
import { CommitSearchResult } from './commit-search-result';
import { ConfluenceDCSearchResult } from './confluence-dc-search-result';
import { ConversationSearchResult } from './conversation-search-result';
import { CustomerOrgSearchResult } from './customer-org-search-result';
import { DashboardSearchResult } from './dashboard-search-result';
import { DataTableSearchResult } from './data-table-search-result';
import { DealSearchResult } from './deal-search-result';
import { DefaultGraphResult } from './default-graph-result';
import { DesignSearchResult } from './design-search-result';
import { DocumentSearchResult } from './document-search-result';
import { MessageSearchResult } from './message-search-result';
import { PullRequestSearchResult } from './pull-request-search-result';
import { RepositorySearchResult } from './repository-search-result';
import { VideoSearchResult } from './video-search-result';
import { WorkItemSearchResult } from './work-item-search-result';

const CONFLUENCE_DC_PRODUCT_KEY = 'confluence-dc';

interface ThirdPartySearchResultProps {
	partial: SearchResultGraphDocument<SearchResultEntity>;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const ThirdPartySearchResult = ({
	partial,
	position,
}: ThirdPartySearchResultProps): React.JSX.Element => {
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const [selectedProducts] = useSelectedProducts();
	const isAllProductSelected = selectedProducts !== undefined && selectedProducts.length === 0;
	const resultAri = AnyAri.parse(partial.id);
	const product = getProductFromProviderId(thirdPartyConfigs, partial.providerId);
	const integrationKey = getIntegrationKey(partial.integrationId);

	const productKey = product || integrationKey;

	const icon = (
		<IconFor3P
			{...{ product: productKey, resourceType: resultAri.resourceType, thirdPartyConfigs }}
		/>
	);

	const metadata = useMemo(() => {
		return {
			id: partial.id,
			containerId: null,
			spaceId: null,
			contentType: partial.subtype,
			entityType: resultAri.resourceType,
			contentId: resultAri.resourceId,
			product: productKey,
			is3PSmartLink: isResultSmartLink({
				connectorType: partial.connectorType,
				resultAri: partial.id,
				productKey,
				thirdPartyConfigs,
			}),
			connectorType: getResultConnectorSource({
				connectorType: partial.connectorType,
				resultNodeId: partial.id,
				productKey,
				thirdPartyConfigs,
			}),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [partial, resultAri, productKey, thirdPartyConfigs]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});

	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	if (isConversationEntity(partial)) {
		return (
			<ConversationSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={productKey}
				resultAri={resultAri}
				onClick={onResultClicked}
				onClickCtxMenu={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				cloudId={cloudId}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}
	if (isDesignEntity(partial)) {
		return (
			<DesignSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isBranchEntity(partial)) {
		return (
			<BranchSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
			/>
		);
	}

	if (isCommitEntity(partial)) {
		return (
			<CommitSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isPullRequestEntity(partial)) {
		return (
			<PullRequestSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isRepositoryEntity(partial)) {
		return (
			<RepositorySearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isVideoEntity(partial)) {
		return (
			<VideoSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
			/>
		);
	}

	if (isMessageEntity(partial)) {
		return (
			<MessageSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isCommentEntity(partial)) {
		return (
			<CommentSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isDocumentEntity(partial)) {
		if (productKey === CONFLUENCE_DC_PRODUCT_KEY) {
			return (
				<ConfluenceDCSearchResult
					partial={partial}
					position={position}
					product={metadata.product}
					onResultClicked={onResultClicked}
					onResultCtxMenuClicked={onResultCtxMenuClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
				/>
			);
		} else {
			return (
				<DocumentSearchResult
					partial={partial}
					position={position}
					icon={icon}
					product={metadata.product}
					onResultClicked={onResultClicked}
					onResultCtxMenuClicked={onResultCtxMenuClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					isAllProductSelected={isAllProductSelected}
				/>
			);
		}
	}

	if (isCalendarEventEntity(partial)) {
		return (
			<CalendarEventSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isDealEntity(partial)) {
		return (
			<DealSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isCustomerOrgEntity(partial)) {
		return (
			<CustomerOrgSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isWorkItemEntity(partial)) {
		return (
			<WorkItemSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
				onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
				onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
				onContextMenuButtonClicked={onContextMenuButtonClicked}
				isAllProductSelected={isAllProductSelected}
			/>
		);
	}

	if (isDashboardEntity(partial)) {
		return (
			<DashboardSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
			/>
		);
	}

	if (isDataTableEntity(partial)) {
		return (
			<DataTableSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={metadata.product}
				onResultClicked={onResultClicked}
				onResultCtxMenuClicked={onResultCtxMenuClicked}
			/>
		);
	}

	return (
		<DefaultGraphResult
			partial={partial as SearchResultGraphDocument<SearchResultEntity>}
			position={position}
			icon={icon}
			entityType={metadata.entityType}
			product={metadata.product}
			onResultClicked={onResultClicked}
			onResultCtxMenuClicked={onResultCtxMenuClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			isAllProductSelected={isAllProductSelected}
		/>
	);
};
